<template>
  <b-container class="square-payments-list-stored-cards-wrapper">
    <!-- Modal components when not inline -->
    <div v-if="showRemoveCardDetails && !inline">
      <square-payments-remove-stored-card-modal
        :account="account"
        :remove-card-details="removeCardDetails"
        :disable-response-modals="disableResponseModals"
        @cancel="closeRemoveCardModal"
        @error="storedCardRemovedError"
        @success="storedCardRemovedSuccess"
      />
    </div>
    <div v-if="showAddCardDetails && !inline">
      <square-payments-add-stored-card-modal
        v-model="showAddCardDetails"
        :account="account"
        :disable-response-modals="disableResponseModals"
        @cancel="() => { showAddCardDetails = false; addCardError = null }"
        @error="storedCardAddError"
        @success="storedCardAddSuccess"
      />
    </div>

    <!-- Loading spinner when inline -->
    <loading-spinner
      v-if="loading && inline"
      class="inline-loading-spinner"
    >
      <template v-slot:title>
        Loading stored cards
      </template>
      Please wait while we load your stored card details...
    </loading-spinner>

    <b-row>
      <b-col v-show="!loading && !shouldHideCardList">
        <div class="stored-card-list-header">
          <slot name="header">
            <h3>
              Your stored credit card details are:
            </h3>
          </slot>
        </div>
        <div
          v-if="hasStoredCardsOnFile"
          class="stored-card-list-message"
        >
          <slot name="message">
            The below credit cards are stored securely in Square's payment gateway and can be used to
            complete purchases, pay for periodic orders, or allow staff to complete a telephone order on
            your behalf.
          </slot>
        </div>
      </b-col>
    </b-row>
    <b-row v-show="!loading && !shouldHideCardList">
      <b-col>
        <div v-if="!hasStoredCardsOnFile">
          <slot name="no-card-message">
            <div class="no-cards-on-file">
              You currently have no stored cards on file.
            </div>
          </slot>
        </div>
        <template v-if="hasStoredCardsOnFile">
          <div :class="`stored-cards-list layout-${size}`">
            <div
              v-for="(card, idx) in storedCardDetails"
              :key="card.id || idx"
              class="stored-card-details"
              :class="`${(idx % 2 === 0) ? 'even' : 'odd'}`"
            >
              <square-payments-storedcard-display
                :account="account"
                :card-details="card"
                :size="size"
              >
                <slot
                  name="card-actions"
                  :card="card"
                />
                <b-button
                  v-if="allowRemoveCard"
                  class="remove-stored-card-button"
                  variant="danger"
                  @click.ctrl.exact="removeCard(card, true)"
                  @click.exact="removeCard(card, false)"
                >
                  Remove card
                </b-button>
              </square-payments-storedcard-display>
            </div>
          </div>
        </template>
        <div
          v-if="allowAddCard"
          class="add-additional-stored-card"
        >
          <b-button
            variant="primary"
            @click="addCard"
          >
            <slot name="add-stored-card-label">
              Add a new stored credit card
            </slot>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Inline add card component -->
    <b-row v-if="showAddCardDetails && inline">
      <b-col>
        <div class="inline-add-card-section">
          <h4>Add New Credit Card</h4>
          <loading-spinner
            v-if="loadingSquare"
            class="square-loading"
          >
            <template v-slot:title>
              Initializing Square payments
            </template>
            Loading secure credit card storage form. Please wait...
          </loading-spinner>

          <loading-spinner
            v-if="addCardProcessing"
            class="add-card-processing"
          >
            <template v-slot:title>
              Saving credit card details.
            </template>
            Please wait while the credit card details are stored.
          </loading-spinner>

          <div v-show="!addCardProcessing">
            <!-- Error display -->
            <b-alert
              v-if="addCardError"
              variant="danger"
              show
              dismissible
              class="add-card-error-alert"
              @dismissed="addCardError = null"
            >
              <h5>
                <font-awesome-icon
                  :icon="['fa', 'exclamation-triangle']"
                  class="error-icon"
                />
                Error storing credit card
              </h5>
              <div v-if="addCardError">
                {{ addCardError }}
              </div>
              <div v-else>
                An error occurred while storing your credit card. Please check your details and try again.
              </div>
            </b-alert>

            <square-payments-store-card
              :account="account"
              :success-component="disableResponseModals ? false : null"
              :failure-component="disableResponseModals ? false : null"
              :error-component="disableResponseModals ? false : null"
              @completed="completeAddCard"
              @error="processAddCardError"
              @processing="(val) => addCardProcessing = val"
              @loading="(val) => loadingSquare = val"
            />
            <div class="cancel-button">
              <b-button
                v-show="!loadingSquare"
                :disabled="addCardProcessing"
                @click="() => { showAddCardDetails = false; addCardError = null }"
              >
                Cancel
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Inline remove card component -->
    <b-row v-if="showRemoveCardDetails && inline">
      <b-col>
        <div class="inline-remove-card-section">
          <h4>
            <font-awesome-icon
              :icon="[ 'fa', 'triangle-exclamation' ]"
              size="lg"
              class="warning-icon"
              fixed-width
              color="#ffcc00"
            />
            Remove Stored Credit Card
          </h4>

          <loading-spinner
            v-if="removeProcessing"
            class="removing-card-processing"
          >
            <template v-slot:title>
              Removing credit card details.
            </template>
            Credit card details being removed. Please wait...
          </loading-spinner>

          <div v-if="!removeProcessing && !requestProcessed">
            <p>
              Do you wish to remove this stored credit card?
            </p>
            <square-payments-storedcard-display
              v-if="account && removeCardDetails"
              :account="account"
              :card-details="removeCardDetails"
              size="md"
            />
            <div v-if="!account">
              <p style="color: red;">
                Error: Account information not available
              </p>
            </div>
            <p class="warning-message">
              This action cannot be undone.
              Once removed you will need to re-enter these details again to continue using this card.
            </p>
            <div class="remove-card-actions">
              <b-button
                variant="primary"
                :disabled="removeProcessing"
                @click="closeRemoveCardModal"
              >
                Cancel
              </b-button>
              <b-button
                variant="danger"
                :disabled="removeProcessing"
                @click="removeCardConfirm"
              >
                Remove stored card
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import RemoveStoredCardModal from '../components/remove-stored-card-modal.vue'
import AddStoredCardModal from '../components/add-stored-card-modal.vue'
import SquarePaymentsStoreCard from './SquarePaymentsStoreCard.vue'

library.add(faExclamationTriangle)

export default {
  name: 'square-payments-list-stored-cards',
  components: {
    'square-payments-remove-stored-card-modal': RemoveStoredCardModal,
    'square-payments-add-stored-card-modal': AddStoredCardModal,
    'square-payments-store-card': SquarePaymentsStoreCard
  },
  props: {
    account: {
      type: Object,
      required: true
    },
    allowAddCard: {
      type: Boolean,
      required: false,
      default: false
    },
    allowRemoveCard: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'lg'
    },
    disableResponseModals: {
      type: Boolean,
      required: false,
      default: false
    },
    inline: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      loading: false,
      storedCardDetails: [],
      removeCardDetails: null,
      showAddCardDetails: false,
      // Inline functionality data
      loadingSquare: true,
      addCardProcessing: false,
      removeProcessing: false,
      processingStart: 0,
      retryableError: false,
      addCardError: null
    }
  },
  computed: {
    showRemoveCardDetails: function() {
      return !!this.removeCardDetails
    },
    hasStoredCardsOnFile: function() {
      return this.loading ? false : this.storedCardDetails.length > 0
    },
    userid: function() {
      return this.account.userid || this.account.user_id || this.account.id || ''
    },
    requestProcessed: function() {
      return this.processingStart > 0
    },
    shouldHideCardList: function() {
      return this.inline && (this.showAddCardDetails || this.showRemoveCardDetails)
    }
  },
  watch: {
    loading: function(newVal, oldVal) {
      this.$emit('loading', newVal)
    }
  },
  mounted: function() {
    this.loadStoredCards()
  },
  methods: {
    addCard: function() {
      this.addCardError = null
      this.retryableError = false
      this.showAddCardDetails = true
    },
    removeCard: function(card, skipConfirmation) {
      // Ensure any existing remove operation is cleared first
      this.removeCardDetails = null
      this.removeProcessing = false
      this.processingStart = 0

      // Set the new remove card details
      this.removeCardDetails = {
        ...card,
        skipConfirmation: skipConfirmation
      }
    },
    storedCardAddError: function(err) {
      this.$emit('error', err)
    },
    storedCardAddSuccess: function(result) {
      // Surface the new card's id before the wallet is refetched, otherwise
      // consumers cannot tell which card was just added. Both the modal path
      // and the inline path (completeAddCard) route through here.
      const cardId = (result && result.card) ? result.card.id : null
      if (cardId) {
        this.$emit('card-added', cardId)
      }
      this.loadStoredCards()
      this.showAddCardDetails = false
    },
    loadStoredCards: function() {
      this.loading = true
      // Reset processing states when reloading cards
      this.processingStart = 0
      this.removeProcessing = false

      this.$store.dispatch('square/loadCustomerCards', { userid: this.userid })
        .then(results => {
          this.loading = false
          this.storedCardDetails = results
        })
        .catch(err => {
          this.loading = false
          this.$emit('error', err)
        })
    },
    storedCardRemovedSuccess: function() {
      this.loadStoredCards()
      this.$emit('remove-card', this.removeCardDetails)
      this.removeCardDetails = null
      this.removeProcessing = false
      this.processingStart = 0
    },
    storedCardRemovedError: function(err) {
      this.$emit('error', err)
      this.removeCardDetails = null
      this.removeProcessing = false
      this.processingStart = 0
    },
    closeRemoveCardModal: function() {
      this.removeCardDetails = null
      this.removeProcessing = false
      this.processingStart = 0
    },
    // Inline add card methods
    completeAddCard: function(modalResult) {
      if (this.retryableError) {
        this.retryableError = false
        this.$store.commit('square/PROCESSING_RESULT')
        this.$emit('error', null)
      }
      else {
        // Get the processing result
        const result = this.$store.getters['square/processingResult']

        // Then emit the appropriate event in the next tick
        this.$nextTick(() => {
          if (result && result.status && result.type === 'card') {
            // Successful card storage - clear any error
            this.addCardError = null
            this.storedCardAddSuccess(result)
          }
          else {
            // Failed or error result
            if (result && result.error) {
              if (Object.prototype.hasOwnProperty.call(result.error, 'reason')) {
                this.addCardError = result.error.reason.replace(/^\w\s-\s/, '')
              }
              else if (Object.prototype.hasOwnProperty.call(result.error, 'message')) {
                this.addCardError = result.error.message.replace(/^\w\s-\s/, '')
              }
              else {
                this.addCardError = result.error
              }
            }
            else if (result) {
              this.addCardError = 'Failed to store credit card. Please check your details and try again.'
            }
            else {
              this.addCardError = 'An unexpected error occurred. Please try again.'
            }
            this.storedCardAddError(result || { error: this.addCardError })
          }
        })
      }
    },
    processAddCardError: function(err) {
      this.addCardProcessing = false
      this.retryableError = true

      // Store the error for display
      if (err) {
        if (Object.prototype.hasOwnProperty.call(err, 'reason')) {
          this.addCardError = err.reason.replace(/^.*?\s-\s/, '')
        }
        else if (Object.prototype.hasOwnProperty.call(err, 'message')) {
          this.addCardError = err.message.replace(/^.*?\s-\s/, '')
        }
        else {
          this.addCardError = err
        }
      }
      else {
        // If no specific error provided, try to get it from the store
        const result = this.$store.getters['square/processingResult']
        if (result && result.error) {
          if (Object.prototype.hasOwnProperty.call(result.error, 'reason')) {
            this.addCardError = result.error.reason.replace(/^.*?\s-\s/, '')
          }
          else if (Object.prototype.hasOwnProperty.call(result.error, 'message')) {
            this.addCardError = result.error.message.replace(/^.*?\s-\s/, '')
          }
          else {
            this.addCardError = result.error
          }
        }
        else {
          this.addCardError = 'An error occurred while processing your credit card. Please try again.'
        }
      }
    },
    // Inline remove card methods
    removeCardConfirm: function() {
      this.processingStart = new Date().getTime()
      this.removeProcessing = true
      this.$store.dispatch('square/removeStoredCard', {
        userid: this.userid,
        cardid: this.removeCardDetails.id
      })
        .then(_ => {
          const processingResult = this.$store.getters['square/processingResult']
          this.$store.commit('square/PROCESSING_RESULT', { type: 'card', ...processingResult })
          this.delayRemoveResponse('success', this.removeCardDetails)
        })
        .catch(err => {
          const processingResult = this.$store.getters['square/processingResult']
          this.$store.commit('square/PROCESSING_RESULT', { type: 'card', ...processingResult })
          this.delayRemoveResponse('error', new Error(`Error removing stored card details: ${err.message}`))
        })
    },
    delayRemoveResponse: function(response, data) {
      const delay = Math.max(100, 2000 - (new Date().getTime() - this.processingStart))

      setTimeout(() => {
        this.removeProcessing = false
        this.processingStart = 0

        if (response === 'success') {
          this.storedCardRemovedSuccess()
        }
        else if (response === 'error') {
          this.storedCardRemovedError(data)
        }
      }, delay)
    }
  }

}
</script>

<style lang="scss" scoped>
  .square-payments-list-stored-cards-wrapper {
    padding: 1.2rem;
    .no-cards-on-file {
      padding: 2.2rem 0;
    }

    .stored-cards-list {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .stored-cards-list {

      .stored-card-details {
        margin: 0.4rem 0.4rem;
        flex-grow: 1;
        flex-shrink: 1;
        flex-basis: 50%;

        .stored-credit-card-details {
          background-color: #eee;
        }
      }
    }

    .details-wrapper {
      display: flex;
      flex-direction: column;

      .card-description {
        margin-bottom: 0.8rem;
      }

      .card-options {
        margin-top: auto;
        display: flex;
        justify-content: flex-end;
        align-self: flex-end;
      }

      .card-options:empty {
        display: none;
      }
    }

    .add-additional-stored-card {
      display: flex;
      justify-content: right;
      margin-top: 1.8rem;
    }

    .card-display-sm {
      .details-wrapper {
        display: flex;
        flex-direction: row;

        .card-description {
          margin-bottom: 0;
        }

        .card-options {
          margin-left: auto;
        }
      }
    }

    // Inline component styles
    .inline-loading-spinner {
      margin: 2rem 0;
    }

    .inline-add-card-section {
      margin: 2rem 0;
      padding: 1.5rem;
      border: 1px solid #ddd;
      border-radius: 0.5rem;
      background-color: #f8f9fa;

      h4 {
        margin-bottom: 1rem;
        color: #333;
      }

      .add-card-error-alert {
        margin-bottom: 1.5rem;

        .error-icon {
          color: #dc3545;
          margin-right: 0.5rem;
        }
      }

      .cancel-button {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        margin-top: 1.8rem;
      }
    }

    .inline-remove-card-section {
      margin: 2rem 0;
      padding: 1.5rem;
      border: 1px solid #ffcc00;
      border-radius: 0.5rem;
      background-color: #fffbf0;

      h4 {
        margin-bottom: 1rem;
        color: #856404;

        .warning-icon {
          margin-right: 0.5rem;
        }
      }

      .warning-message {
        color: #856404;
        font-weight: bold;
        margin: 1rem 0;
      }

      .remove-card-actions {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        gap: 0.5rem;
        margin-top: 1.5rem;
      }
    }
  }
</style>
